/**
 * Playbooks QA round 8, U1 (items 1, 5) — pure, DOM-free helpers behind
 * `PlaybooksHome`'s List view window (default to campaigns dated
 * today − 14 days onward, plus undated ones, widening to everything on
 * search), its "Today" anchor, and its StrictMode-safe single `{year,
 * month0}` view-month state (replaces the paired `viewYear`/`viewMonth`
 * whose nested `setViewYear` call inside a `setViewMonth` updater double-runs
 * under React StrictMode's dev double-invoke, corrupting the year by ±2
 * across a December↔January rollover).
 *
 * Reuses `dates.ts`'s `parseDateOnly`/`todayIso` — no second date parser.
 */
import type { Campaign } from '../_shared/launcher-campaign.types';
import { parseDateOnly } from './dates';

/** Days of history the un-searched Home list keeps (Spencer, 2026-09-22). */
export const HOME_LOOKBACK_DAYS = 14;

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** `YYYY-MM-DD` `days` before `todayIso` — pure string/date arithmetic on a date-only value (no TZ drift). */
export function lookbackCutoffIso(todayIso: string, days: number = HOME_LOOKBACK_DAYS): string {
  const d = parseDateOnly(todayIso);
  if (!d) {
    throw new Error(`lookbackCutoffIso: invalid date-only string "${todayIso}"`);
  }
  d.setDate(d.getDate() - days);
  return toIso(d);
}

function matchesSearch(campaign: Campaign, query: string): boolean {
  if (!query) return true;
  return (
    campaign.name.toLowerCase().includes(query) ||
    campaign.type.toLowerCase().includes(query) ||
    (campaign.subtype ?? '').toLowerCase().includes(query)
  );
}

/** Ascending by `event_date`, undated last — the display order both the List and Calendar want. */
function byEventDateAscUndatedLast(a: Campaign, b: Campaign): number {
  if (a.event_date == null && b.event_date == null) return 0;
  if (a.event_date == null) return 1;
  if (b.event_date == null) return -1;
  return a.event_date.localeCompare(b.event_date);
}

/**
 * The list Home renders. With NO search: campaigns whose `event_date >= cutoff`, plus undated
 * campaigns (they group under "No Date" and would otherwise vanish). With a search: EVERY campaign
 * matching name/type/subtype (case-insensitive), so old campaigns stay findable. Always sorted
 * ascending by `event_date` with undated last (today's sort, `PlaybooksHome.tsx:303`).
 */
export function homeCampaignWindow(
  campaigns: readonly Campaign[],
  search: string,
  todayIso: string,
): { campaigns: Campaign[]; windowed: boolean; cutoffIso: string } {
  const query = search.trim().toLowerCase();
  const windowed = query.length === 0;
  const cutoffIso = lookbackCutoffIso(todayIso);

  const result = campaigns
    .filter((c) => {
      if (!matchesSearch(c, query)) return false;
      if (!windowed) return true; // searching widens to everything matched, regardless of date
      if (c.event_date == null) return true; // undated campaigns always stay in the un-searched window
      return c.event_date >= cutoffIso;
    })
    .slice()
    .sort(byEventDateAscUndatedLast);

  return { campaigns: result, windowed, cutoffIso };
}

/**
 * The month key the "Today" button and the initial list anchor scroll to: the current month when a
 * group exists for it, else the FIRST group key on/after it, else `null` (nothing to scroll —
 * every group is older, or the list is empty). Keys are `monthKey()` strings (`YYYY-MM`, `9999-99` for
 * undated) and MUST be passed sorted ascending — the same order `grouped` renders.
 */
export function anchorMonthKey(groupKeys: readonly string[], todayIso: string): string | null {
  if (groupKeys.length === 0) return null;
  const currentKey = todayIso.slice(0, 7);
  for (const key of groupKeys) {
    if (key >= currentKey) return key;
  }
  return null;
}

/** One state value, one updater — replaces the paired `viewYear`/`viewMonth` (StrictMode-safe). */
export interface ViewMonth {
  year: number;
  month0: number;
}

export function shiftViewMonth(view: ViewMonth, delta: 1 | -1): ViewMonth {
  let { year, month0 } = view;
  month0 += delta;
  if (month0 > 11) {
    month0 = 0;
    year += 1;
  } else if (month0 < 0) {
    month0 = 11;
    year -= 1;
  }
  return { year, month0 };
}

export function viewMonthFromIso(iso: string): ViewMonth {
  const [year, month] = iso.split('-').map(Number);
  return { year, month0: month - 1 };
}

export function viewMonthKey(view: ViewMonth): string {
  return `${view.year}-${String(view.month0 + 1).padStart(2, '0')}`;
}
